export const TOURI_SESSION_STATUSES = {
    ACTIVE: 'active',
    PROCESSING: 'processing',
    ARCHIVED: 'archived',
    DELETED: 'deleted',
} as const;

export type TouriSessionStatus = (typeof TOURI_SESSION_STATUSES)[keyof typeof TOURI_SESSION_STATUSES];

/** Backend / UI display hint for rich chat blocks */
export const TOURI_DISPLAY_HINTS = {
    RATE_TABLE: 'rate_table',
    COMPARISON_TABLE: 'comparison_table',
    PROPERTY_CARD: 'property_card',
    PROPERTY_LIST: 'property_list',
    PROPERTY_MAP: 'property_map',
    SERVICE_TABLE: 'service_table',
    AVAILABILITY: 'availability',
    SPECIALS_LIST: 'specials_list',
    TEXT: 'text',
} as const;

export type DisplayHint = (typeof TOURI_DISPLAY_HINTS)[keyof typeof TOURI_DISPLAY_HINTS];

/** Hints that show copy / Excel toolbar on rich blocks */
export const TOURI_RICH_TABLE_TOOLBAR_HINTS = [
    TOURI_DISPLAY_HINTS.RATE_TABLE,
    TOURI_DISPLAY_HINTS.COMPARISON_TABLE,
    TOURI_DISPLAY_HINTS.PROPERTY_LIST,
    TOURI_DISPLAY_HINTS.PROPERTY_MAP,
    TOURI_DISPLAY_HINTS.SERVICE_TABLE,
] as const;

/** Hints eligible for rate comparison chart (tabular prices) */
export const TOURI_RICH_CHART_RATE_HINTS = [TOURI_DISPLAY_HINTS.RATE_TABLE, TOURI_DISPLAY_HINTS.COMPARISON_TABLE] as const;

export const isTouriRichTableToolbarHint = (hint: string): boolean => (TOURI_RICH_TABLE_TOOLBAR_HINTS as readonly string[]).includes(hint);

export const isTouriRichChartRateHint = (hint: string): boolean => (TOURI_RICH_CHART_RATE_HINTS as readonly string[]).includes(hint);

/** SSE payload `type` values from the Touri stream endpoint */
export const TOURI_STREAM_EVENT_TYPES = {
    START: 'start',
    /** Which stage the AI service is on (`stage` key, translated via `touri.stages.*`). */
    STATUS: 'status',
    TOKEN: 'token',
    CHUNK: 'chunk',
    /** A later answer call superseded the streamed text; clear it and keep listening. */
    ANSWER_RESET: 'answer_reset',
    /** The finished answer text; supersedes the accumulated tokens. */
    ANSWER: 'answer',
    METADATA: 'metadata',
    /** Early entity rows, emitted before the token stream so mentions can be hyperlinked live. */
    ENTITIES: 'entities',
    SESSION_UPDATE: 'session_update',
    STRUCTURED_DATA: 'structured_data',
    COMPLETE: 'complete',
    ERROR: 'error',
} as const;

export type TouriStreamEventType = (typeof TOURI_STREAM_EVENT_TYPES)[keyof typeof TOURI_STREAM_EVENT_TYPES];

export const TOURI_FEEDBACK_RATINGS = {
    POSITIVE: 'positive',
    NEGATIVE: 'negative',
} as const;

export type TouriFeedbackRating = (typeof TOURI_FEEDBACK_RATINGS)[keyof typeof TOURI_FEEDBACK_RATINGS];

/** Total messages in a thread before the "how helpful?" prompt appears (once per session). */
export const TOURI_SESSION_FEEDBACK_PROMPT_THRESHOLD = 5;

/** API error payloads / client-side error codes */
export const TOURI_CHAT_API_ERRORS = {
    TOKEN_LIMIT_EXCEEDED: 'chat_token_limit_exceeded',
} as const;

export const TOURI_CHAT_ERROR_NAMES = {
    TOKEN_LIMIT: 'ChatTokenLimitError',
} as const;

export const TOURI_STREAM_RETRY = {
    MAX_ATTEMPTS: 3,
    BASE_DELAY_MS: 1000,
} as const;

export const TOURI_SSE = {
    DATA_PREFIX: 'data: ',
} as const;

export const TOURI_REQUEST_HEADERS = {
    CONTENT_TYPE_JSON: 'application/json',
    ACCEPT_EVENT_STREAM: 'text/event-stream',
    X_REQUESTED_WITH: 'XMLHttpRequest',
} as const;

/** Fallback palette when CSS variables are unavailable (e.g. SSR) */
export const TOURI_CHART_SSR_FALLBACK_COLORS = [
    'hsl(220, 70%, 50%)',
    'hsl(160, 60%, 45%)',
    'hsl(30, 80%, 55%)',
    'hsl(280, 65%, 60%)',
    'hsl(340, 75%, 55%)',
] as const;

/** Font stack matching the project's `--font-sans`, used by chart canvas labels where CSS doesn't apply */
export const TOURI_CHART_FONT_FAMILY = "'Comfortaa', ui-sans-serif, system-ui, sans-serif" as const;

/** Foreground colour fallback for chart text when `--foreground` is unavailable (matches light-mode token) */
export const TOURI_CHART_FALLBACK_FOREGROUND = 'hsl(20, 15%, 12%)' as const;

/** Grid line colour fallback for charts when `--border` is unavailable */
export const TOURI_CHART_FALLBACK_GRID = 'hsla(20, 15%, 12%, 0.12)' as const;

export const TOURI_DEFAULT_CURRENCY = 'USD';

/**
 * The Touri page's Inertia component — the same value as
 * `App\Constants\InertiaPages::TOURI_INDEX`. The header slide-out hides
 * itself there, since the page already is the full-size chat.
 */
export const TOURI_PAGE_COMPONENT = 'tenant/touri/Index';
